import React, { useState, useEffect, useRef } from 'react';
import { ChevronRight } from 'lucide-react';
import Accordion from '../Accordion';
import { useDesignMode } from '../../contexts/DesignContext';
import { classNames } from '../../utils/classNames';
import { SketchBox } from './SketchBox';

interface SketchSectionProps {
  title: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  count?: number;
  icon?: React.ReactNode;
  indicator?: React.ReactNode;
  defaultOpen?: boolean;
  /** Stable seed for section box tilt (defaults to title string). */
  tiltSeed?: string | number;
}

/** Collapsible section; sketch-box styling in Sketchy mode, Accordion in Classic. */
export const SketchSection: React.FC<SketchSectionProps> = ({
  title,
  children,
  className,
  count,
  icon,
  indicator,
  defaultOpen = true,
  tiltSeed,
}) => {
  const { isSketchy } = useDesignMode();
  const autoId = React.useId();
  const seed = tiltSeed ?? (typeof title === 'string' ? title : autoId);

  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [contentHeight, setContentHeight] = useState<number | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const measureContentHeight = () => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight + 20);
    }
  };

  useEffect(() => {
    setIsOpen(defaultOpen);
  }, [defaultOpen]);

  useEffect(() => {
    measureContentHeight();
  }, [children, isOpen]);

  useEffect(() => {
    if (!contentRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      measureContentHeight();
    });

    resizeObserver.observe(contentRef.current);

    const observeChildren = () => {
      contentRef.current?.querySelectorAll('*').forEach((child) => {
        resizeObserver.observe(child);
      });
    };

    observeChildren();

    const mutationObserver = new MutationObserver(observeChildren);
    mutationObserver.observe(contentRef.current, { childList: true, subtree: true });

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, []);

  const toggleOpen = () => {
    setIsOpen((open) => {
      const next = !open;
      if (next && contentRef.current) {
        setTimeout(measureContentHeight, 50);
      }
      return next;
    });
  };

  if (!isSketchy) {
    return (
      <Accordion
        title={title}
        defaultOpen={defaultOpen}
        className={className}
        count={count}
        icon={icon}
        indicator={indicator}
      >
        {children}
      </Accordion>
    );
  }

  return (
    <SketchBox
      tilt="auto"
      tiltSeed={seed}
      className={classNames('w-full mb-4', className)}
      data-is-open={isOpen}
    >
      <div
        className="sketch-section-header cursor-pointer select-none flex items-center justify-between gap-3 px-4 sm:px-6 py-3 sm:py-4"
        onClick={toggleOpen}
        data-testid="accordion-button"
        data-is-open={isOpen}
        aria-expanded={isOpen}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleOpen();
          }
        }}
      >
        <div className="flex items-center gap-2">
          {icon && <div className="flex-shrink-0">{icon}</div>}
          <div className="text-base sm:text-lg font-semibold text-[var(--color-text)]">{title}</div>
          {!isOpen && indicator && <div className="flex-shrink-0">{indicator}</div>}
          {!isOpen && !indicator && count !== undefined && count > 0 && (
            <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-bold rounded-full bg-[var(--brand-from)] text-white">
              {count}
            </span>
          )}
        </div>
        <ChevronRight
          className={classNames(
            'transition-transform duration-200 text-[var(--color-text)]/70',
            isOpen && 'rotate-90'
          )}
          size={20}
          aria-hidden
        />
      </div>
      <div
        ref={contentRef}
        data-testid="accordion-content"
        data-is-open={isOpen}
        style={{
          maxHeight: isOpen ? (contentHeight != null ? `${contentHeight}px` : 'none') : '0',
          overflow: 'hidden',
          transition: contentHeight != null ? 'max-height 0.3s ease-out' : undefined,
          width: '100%',
        }}
      >
        <div className="sketch-section-content">{children}</div>
      </div>
    </SketchBox>
  );
};
